import {
  getCloudFrontImageUrl,
  getCloudFrontOriginalImageUrl,
  getDirectS3StudioImageUrl,
} from './cloudFrontUtils';
import fallbackImage from '../public/noImage.webp';

/**
 * Ordered candidate URLs for the studio's first cover image.
 * Studio profile hero (Studio.jsx `previewGalleryImages`) uses resized CloudFront URLs
 * built from `originalImages[0]`; `useStudio`’s `processedImages[0]` is format-only WebP
 * and can fail in the browser while the resized URL works.
 *
 * @param {{ originalImages?: string[], processedImages?: string[] } | null | undefined} studioLike
 * @returns {string[]}
 */
export function buildStudioCoverImageCandidates(studioLike) {
  const out = [];
  const seen = new Set();
  const push = (u) => {
    if (typeof u === 'string' && u.length > 0 && !seen.has(u)) {
      seen.add(u);
      out.push(u);
    }
  };

  const key0 = studioLike?.originalImages?.[0];
  if (key0) {
    try {
      push(
        getCloudFrontImageUrl(key0, {
          width: 1800,
          height: 1350,
          fit: 'cover',
          quality: 95,
        })
      );
    } catch {
      // ignore
    }
  }

  push(studioLike?.processedImages?.[0]);

  if (key0) {
    push(getCloudFrontOriginalImageUrl(key0));
    push(getDirectS3StudioImageUrl(key0));
  }

  return out;
}

/**
 * Returns a safe studio image URL with a consistent fallback.
 *
 * @param {string | null | undefined} primary      - Preferred image (e.g. explicit conversation image).
 * @param {string | null | undefined} secondary    - Optional secondary image (e.g. first studio image).
 * @returns {string}                               - URL to use in <img src="...">.
 */
export const getStudioImageSrc = (primary, secondary) => {
  const candidate = primary || secondary;

  if (!candidate) {
    return fallbackImage;
  }

  // Already a full URL or base64 data URL – use as-is
  if (typeof candidate === 'string' && (candidate.startsWith('http') || candidate.startsWith('data:'))) {
    return candidate;
  }

  // Treat as S3 key / relative key for CloudFront transformation
  try {
    return getCloudFrontImageUrl(candidate);
  } catch (error) {
    // As a last resort, fall back to the static placeholder image
    return fallbackImage;
  }
};


